import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FileText, X, CheckCircle2, Save, AlertCircle, Calendar, Target,
  Clock, AlertTriangle, ShieldCheck, Award, RefreshCw, Layers, ChevronLeft
} from 'lucide-react';
import {
  Client, Provider, UserSession,
  EvalSectionData, PocSectionData, QprSectionData
} from '../types';
import { EvalWorkspace } from './clinical/EvalWorkspace';
import { PocWorkspace } from './clinical/PocWorkspace';
import { QprWorkspace } from './clinical/QprWorkspace';
import { ManualSoapWorkspace, SoapSectionData } from './clinical/ManualSoapWorkspace';
import { ComplianceAuditModal } from './clinical/ComplianceAuditModal';
import { AiRefineModal, RefineTargetField } from './clinical/AiRefineModal';
import { MissingEvidenceModal } from './clinical/MissingEvidenceModal';
import { playPulseSeal } from '../utils/pulseAudio';
import { authoredClinicalDocType, defaultSoapTitle, evaluationAuthoringSaveFields, parseAuthoredEvalTreatmentSetting, planMedicalNecessityForSave, soapTitleAfterDefaultRefresh } from '../utils/clinicalDocDisplay';
import {
  DEFAULT_SKILLED_NECESSITY,
  authoringForSelectedClient,
  canonicalGoalsFromClientDetail,
  clientLoadIsCurrent,
  pocGoalsFromClientGoals,
  qprReviewsFromClientGoals,
  soapTargetsFromClientGoals,
  withClientChartOnEval,
  withClientChartOnPoc,
  withClientChartOnQpr,
  withClientChartOnSoap,
  createInitialEvalAuthoring,
  createInitialPocAuthoring,
  createInitialQprAuthoring,
  createInitialSoapAuthoring
} from '../utils/clinicalAuthoringClient';

interface CompatibleSectionConfig {
  key: string;
  title: string;
  pairedKey?: string;
  pairedTitle?: string;
}

const getCompatibleSectionsForDocType = (type: 'EVAL' | 'POC' | 'QPR' | 'SOAP'): CompatibleSectionConfig[] => {
  switch (type) {
    case 'EVAL':
      return [
        { key: 'caregiver_concerns', title: 'Caregiver Concerns', pairedKey: 'direct_observations', pairedTitle: 'Direct Observations' },
        { key: 'direct_observations', title: 'Direct Observations', pairedKey: 'caregiver_concerns', pairedTitle: 'Caregiver Concerns' },
        { key: 'receptive_language', title: 'Receptive Language', pairedKey: 'expressive_language', pairedTitle: 'Expressive Language' },
        { key: 'expressive_language', title: 'Expressive Language', pairedKey: 'receptive_language', pairedTitle: 'Receptive Language' },
        { key: 'speech_production', title: 'Speech Sound Production', pairedKey: 'pragmatic_social', pairedTitle: 'Pragmatics & Social' },
        { key: 'diagnostic_impressions', title: 'Diagnostic Impressions', pairedKey: 'medical_necessity_rationale', pairedTitle: 'Medical Necessity Rationale' },
        { key: 'medical_necessity_rationale', title: 'Medical Necessity Rationale', pairedKey: 'diagnostic_impressions', pairedTitle: 'Diagnostic Impressions' },
        { key: 'home_program_strategies', title: 'Home Program Strategies', pairedKey: 'caregiver_training_plan', pairedTitle: 'Caregiver Training Plan' }
      ];
    case 'POC':
      return [
        { key: 'progress_summary', title: 'Progress Summary', pairedKey: 'continued_skilled_need', pairedTitle: 'Continued Skilled Need' },
        { key: 'continued_skilled_need', title: 'Continued Skilled Need', pairedKey: 'progress_summary', pairedTitle: 'Progress Summary' },
        { key: 'caregiver_participation', title: 'Caregiver Participation', pairedKey: 'home_program_carryover', pairedTitle: 'Home Program Carryover' },
        { key: 'home_program_carryover', title: 'Home Program Carryover', pairedKey: 'caregiver_participation', pairedTitle: 'Caregiver Participation' },
        { key: 'medical_necessity_statement', title: 'Medical Necessity Statement', pairedKey: 'continued_skilled_need', pairedTitle: 'Continued Skilled Need' }
      ];
    case 'QPR':
      return [
        { key: 'therapy_summary', title: 'Therapy Summary', pairedKey: 'skilled_care_justification', pairedTitle: 'Skilled Care Justification' },
        { key: 'skilled_care_justification', title: 'Skilled Care Justification', pairedKey: 'therapy_summary', pairedTitle: 'Therapy Summary' },
        { key: 'caregiver_training_notes', title: 'Caregiver Training Notes' },
        { key: 'recommendations_summary', title: 'Recommendations & Medical Necessity', pairedKey: 'skilled_care_justification', pairedTitle: 'Skilled Care Justification' }
      ];
    case 'SOAP':
      return [
        { key: 'subjective', title: 'Subjective (S)', pairedKey: 'assessment', pairedTitle: 'Assessment (A)' },
        { key: 'objective', title: 'Objective (O)', pairedKey: 'assessment', pairedTitle: 'Assessment (A)' },
        { key: 'assessment', title: 'Assessment (A)', pairedKey: 'plan', pairedTitle: 'Plan (P)' },
        { key: 'plan', title: 'Plan (P)', pairedKey: 'assessment', pairedTitle: 'Assessment (A)' },
        { key: 'medical_necessity', title: 'Skilled Care Justification', pairedKey: 'assessment', pairedTitle: 'Assessment (A)' }
      ];
  }
};

// Document-Type Color Identities Matching Clinical Launch Cards:
// • Evaluation → cool blue / cyan-indigo
// • Plan of Care / POC Recertification → violet / indigo
// • Quarterly Progress Report (QPR) → emerald / teal-green
// • Daily Treatment SOAP → electric cyan / blue
const DOC_TYPE_THEMES: Record<'EVAL' | 'POC' | 'QPR' | 'SOAP', {
  label: string;
  badgeLabel: string;
  headerBadgeClasses: string;
  headerIconBoxClasses: string;
  tileSelectedBorder: string;
  tileSelectedBg: string;
  tileSelectedRing: string;
  tileSelectedShadow: string;
  tileSpecularLine: string;
  tileGlowBlur: string;
  tileIconColor: string;
  icon: React.ComponentType<{ className?: string }>;
}> = {
  EVAL: {
    label: 'Evaluation & POC',
    badgeLabel: 'EVAL + POC',
    headerBadgeClasses: 'bg-blue-500/20 text-blue-300 border-blue-500/35',
    headerIconBoxClasses: 'bg-blue-500/15 border-blue-500/30 text-blue-300',
    tileSelectedBorder: 'border-blue-300',
    tileSelectedBg: 'bg-blue-50',
    tileSelectedRing: 'ring-2 ring-blue-200',
    tileSelectedShadow: 'shadow-xs',
    tileSpecularLine: 'via-blue-300/60',
    tileGlowBlur: 'bg-blue-500/10',
    tileIconColor: 'text-blue-600',
    icon: Award,
  },
  POC: {
    label: 'POC Recertification',
    badgeLabel: 'POC RECERTIFICATION',
    headerBadgeClasses: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/35',
    headerIconBoxClasses: 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300',
    tileSelectedBorder: 'border-indigo-300',
    tileSelectedBg: 'bg-indigo-50',
    tileSelectedRing: 'ring-2 ring-indigo-200',
    tileSelectedShadow: 'shadow-xs',
    tileSpecularLine: 'via-indigo-300/60',
    tileGlowBlur: 'bg-indigo-500/10',
    tileIconColor: 'text-indigo-600',
    icon: Calendar,
  },
  QPR: {
    label: 'Quarterly Progress (QPR)',
    badgeLabel: 'QUARTERLY PROGRESS',
    headerBadgeClasses: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/35',
    headerIconBoxClasses: 'bg-emerald-500/15 border-emerald-400/30 text-emerald-300',
    tileSelectedBorder: 'border-emerald-300',
    tileSelectedBg: 'bg-emerald-50',
    tileSelectedRing: 'ring-2 ring-emerald-200',
    tileSelectedShadow: 'shadow-xs',
    tileSpecularLine: 'via-emerald-300/60',
    tileGlowBlur: 'bg-emerald-500/10',
    tileIconColor: 'text-emerald-600',
    icon: Target,
  },
  SOAP: {
    label: 'Daily Treatment SOAP',
    badgeLabel: 'DAILY SOAP',
    headerBadgeClasses: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/35',
    headerIconBoxClasses: 'bg-cyan-500/15 border-cyan-400/30 text-cyan-300',
    tileSelectedBorder: 'border-cyan-300',
    tileSelectedBg: 'bg-cyan-50',
    tileSelectedRing: 'ring-2 ring-cyan-200',
    tileSelectedShadow: 'shadow-xs',
    tileSpecularLine: 'via-cyan-300/60',
    tileGlowBlur: 'bg-cyan-500/10',
    tileIconColor: 'text-cyan-600',
    icon: FileText,
  },
};

interface NewClinicalDocModalProps {
  user: UserSession;
  onClose: () => void;
  onSuccess: (docId: string) => void;
  initialDocType?: 'EVAL' | 'POC' | 'QPR' | 'SOAP';
  initialClientId?: string;
  initialDos?: string;
  initialStartTime?: string;
  initialProviderId?: string;
  initialServiceCode?: string;
}

interface CanonicalGoal {
  id: string;
  description: string;
  baseline_performance?: string;
  target_percentage?: number;
  current_percentage?: number;
  status?: string;
}

export const NewClinicalDocModal: React.FC<NewClinicalDocModalProps> = ({
  user,
  onClose,
  onSuccess,
  initialDocType,
  initialClientId,
  initialDos,
  initialStartTime,
  initialProviderId,
  initialServiceCode
}) => {
  const isAssistantUser = user?.role === 'ASSISTANT';
  const initialValidType: 'EVAL' | 'POC' | 'QPR' = isAssistantUser
    ? 'QPR'
    : (initialDocType === 'POC' || initialDocType === 'QPR' ? initialDocType : 'EVAL');

  const [docType, setDocType] = useState<'EVAL' | 'POC' | 'QPR' | 'SOAP'>(initialValidType);
  const [clients, setClients] = useState<Client[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(initialClientId || '');
  const [selectedProviderId, setSelectedProviderId] = useState(initialProviderId || '');
  const [title, setTitle] = useState('');
  const previousDocType = useRef<'EVAL' | 'POC' | 'QPR' | 'SOAP' | null>(null);
  const boundClientRef = useRef('');
  const chartAppliedRef = useRef('');
  const clientLoadTokenRef = useRef(0);
  const authoringGoalsReadyRef = useRef({ clientId: '', qpr: false, poc: false });

  // Mobile viewport detection for compact selector labels (mobile-only option text)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Client canonical goals loaded from client chart
  const [clientGoals, setClientGoals] = useState<CanonicalGoal[]>([]);
  const [loadingGoals, setLoadingGoals] = useState(false);

  // AI & Compliance Modal State
  const [showComplianceAudit, setShowComplianceAudit] = useState(false);

  const [evalData, setEvalData] = useState<EvalSectionData>(() => createInitialEvalAuthoring());
  const [pocData, setPocData] = useState<PocSectionData>(() => createInitialPocAuthoring());
  const [qprData, setQprData] = useState<QprSectionData>(() => createInitialQprAuthoring());

  // Common SOAP fallback fields
  const [subjective, setSubjective] = useState('');
  const [objective, setObjective] = useState('');
  const [assessment, setAssessment] = useState('');
  const [plan, setPlan] = useState('');
  const [medicalNecessity, setMedicalNecessity] = useState(DEFAULT_SKILLED_NECESSITY);

  // Helper to calculate timeOut given timeIn and duration in minutes
  const calculateTimeOut = (startTime: string, minutes: number): string => {
    const [h, m] = (startTime || '09:00').split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return '10:00';
    const totalM = h * 60 + m + (minutes || 60);
    const newH = Math.floor((totalM / 60) % 24);
    const newM = totalM % 60;
    return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
  };

  // SOAP Specific fields
  const [dos, setDos] = useState(initialDos || new Date().toISOString().slice(0, 10));
  const [timeIn, setTimeIn] = useState(initialStartTime || '09:00');
  const timeInRef = useRef(timeIn);
  timeInRef.current = timeIn;
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [timeOut, setTimeOut] = useState(() => calculateTimeOut(initialStartTime || '09:00', 60));
  const [serviceCode, setServiceCode] = useState(initialServiceCode || '92507');
  const [soapTargetedGoals, setSoapTargetedGoals] = useState<{ [goalId: string]: { targeted: boolean; accuracy: number; cueing: string } }>({});

  // Manual SOAP Workspace Data (Aligning with Mobile SOAP specification)
  const [soapData, setSoapData] = useState<SoapSectionData>(() => createInitialSoapAuthoring({
    dos: initialDos,
    timeIn: initialStartTime,
    serviceCode: initialServiceCode
  }));

  const [saving, setSaving] = useState(false);

  // AI Clinical Assist Launcher State
  const [showAiAssistMenu, setShowAiAssistMenu] = useState(false);
  const [selectedSectionKey, setSelectedSectionKey] = useState<string | null>(null);
  const [showMissingEvidenceModal, setShowMissingEvidenceModal] = useState(false);
  const [refineModalState, setRefineModalState] = useState<{
    isOpen: boolean;
    sectionKey: string;
    sectionTitle: string;
    currentText: string;
    pairedFields?: [RefineTargetField, RefineTargetField];
    initialMode: 'SINGLE' | 'PAIRED';
  } | null>(null);

  useEffect(() => {
    // Reset selection when docType changes
    setSelectedSectionKey(null);
  }, [docType]);

  const compatibleSections = useMemo(() => getCompatibleSectionsForDocType(docType), [docType]);
  const activeSectionConfig = useMemo(() => {
    if (!selectedSectionKey) return null;
    return compatibleSections.find(s => s.key === selectedSectionKey) || null;
  }, [selectedSectionKey, compatibleSections]);

  const getSectionText = (key: string): string => {
    if (docType === 'EVAL') {
      return (evalData as any)[key] || '';
    } else if (docType === 'POC') {
      return (pocData as any)[key] || '';
    } else if (docType === 'QPR') {
      return (qprData as any)[key] || '';
    } else if (docType === 'SOAP') {
      if (key === 'subjective') return soapData.subjective || subjective;
      if (key === 'objective') return soapData.objective || objective;
      if (key === 'assessment') return soapData.assessment || assessment;
      if (key === 'plan') return soapData.plan || plan;
      if (key === 'medical_necessity') return soapData.medical_necessity || medicalNecessity;
    }
    return '';
  };

  const handleApplySectionText = (key: string, newText: string) => {
    if (docType === 'EVAL') {
      setEvalData(prev => ({ ...prev, [key]: newText }));
    } else if (docType === 'POC') {
      setPocData(prev => ({ ...prev, [key]: newText }));
    } else if (docType === 'QPR') {
      setQprData(prev => ({ ...prev, [key]: newText }));
    } else if (docType === 'SOAP') {
      setSoapData(prev => ({ ...prev, [key]: newText }));
      if (key === 'subjective') setSubjective(newText);
      else if (key === 'objective') setObjective(newText);
      else if (key === 'assessment') setAssessment(newText);
      else if (key === 'plan') setPlan(newText);
      else if (key === 'medical_necessity') setMedicalNecessity(newText);
    }
  };

  const handleLaunchAssist = (mode: 'SINGLE' | 'PAIRED') => {
    if (!activeSectionConfig) return;
    const currentText = getSectionText(activeSectionConfig.key);

    let pairedFields: [RefineTargetField, RefineTargetField] | undefined = undefined;
    if (activeSectionConfig.pairedKey) {
      pairedFields = [
        {
          sectionKey: activeSectionConfig.key,
          sectionTitle: activeSectionConfig.title,
          currentText
        },
        {
          sectionKey: activeSectionConfig.pairedKey,
          sectionTitle: activeSectionConfig.pairedTitle || activeSectionConfig.pairedKey,
          currentText: getSectionText(activeSectionConfig.pairedKey)
        }
      ];
    }

    setRefineModalState({
      isOpen: true,
      sectionKey: activeSectionConfig.key,
      sectionTitle: activeSectionConfig.title,
      currentText,
      pairedFields,
      initialMode: mode
    });
    setShowAiAssistMenu(false);
  };

  // Assignment check for Manual SOAP exception lane
  const [clientAssignment, setClientAssignment] = useState<any>(null);
  const [checkingAssignment, setCheckingAssignment] = useState(false);

  useEffect(() => {
    if (!selectedClientId) {
      setClientAssignment(null);
      return;
    }
    let cancelled = false;
    const requestedClientId = selectedClientId;
    const loadAssignment = async () => {
      setCheckingAssignment(true);
      try {
        const res = await fetch('/api/assignments');
        if (!res.ok || cancelled || requestedClientId !== selectedClientId) return;
        const list = await res.json();
        if (cancelled || requestedClientId !== selectedClientId) return;
        const match = list.find((a: any) =>
          a.client_id === requestedClientId &&
          a.provider_id === selectedProviderId &&
          a.active !== 0
        );
        setClientAssignment(match || null);
      } catch (err) {
        if (!cancelled) console.warn('Could not load assignment:', err);
      } finally {
        if (!cancelled) setCheckingAssignment(false);
      }
    };
    loadAssignment();
    return () => {
      cancelled = true;
    };
  }, [selectedClientId, selectedProviderId]);

  const isManualSoapApproved = Boolean(clientAssignment?.manual_soap_approved);
  const manualSoapReason = clientAssignment?.manual_soap_reason;

  // Initial load of clients and providers
  useEffect(() => {
    const loadResources = async () => {
      try {
        const [cRes, pRes] = await Promise.all([
          fetch('/api/clients'),
          fetch('/api/providers')
        ]);
        if (cRes.ok) {
          const cData = await cRes.json();
          setClients(cData);
          const allowed = user?.role === 'ADMIN'
            ? cData
            : cData.filter((c: any) => (c.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
          if (allowed.length > 0) {
            setSelectedClientId(prev => prev || initialClientId || allowed[0].id);
          }
        }
        if (pRes.ok) {
          const pData = await pRes.json();
          setProviders(pData);
          if (pData.length > 0) {
            let defaultP = initialProviderId || '';
            if (isAssistantUser || user?.role === 'SUPERVISOR') {
              const myP = pData.find((p: any) => p.user_id === user?.id || (user?.email && p.email?.toLowerCase() === user.email.toLowerCase()));
              if (myP) defaultP = myP.id;
            }
            setSelectedProviderId(prev => prev || defaultP);
          }
        }
      } catch (err) {
        console.error('Error loading resources:', err);
      }
    };
    loadResources();
  }, [initialClientId, initialProviderId, user?.role, user?.id, user?.email, isAssistantUser]);

  // Provider filtering by Document Workflow:
  // EVAL and POC establish or recertify canonical treatment plans and must be authored by a licensed SLP.
  // SOAP and QPR allow SLPA or SLP authors.
  // For assistant users, only their own linked provider profile is eligible.
  const eligibleProviders = useMemo(() => {
    if (isAssistantUser) {
      return providers.filter(p => p.user_id === user?.id || (user?.email && p.email?.toLowerCase() === user.email.toLowerCase()));
    }
    if (docType === 'EVAL' || docType === 'POC') {
      return providers.filter(p => p.role === 'SLP');
    }
    return providers;
  }, [providers, docType, isAssistantUser, user?.id, user?.email]);

  // Guard: For assistant users, always lock provider to their own profile.
  // For other users, if switching to EVAL or POC while an ineligible provider (e.g. SLPA) is selected,
  // automatically select the first eligible licensed SLP.
  useEffect(() => {
    if (isAssistantUser) {
      const myProv = providers.find(p => p.user_id === user?.id || (user?.email && p.email?.toLowerCase() === user.email.toLowerCase()));
      if (myProv && selectedProviderId !== myProv.id) {
        setSelectedProviderId(myProv.id);
      }
    } else if (docType === 'EVAL' || docType === 'POC') {
      const current = providers.find(p => p.id === selectedProviderId);
      if (selectedProviderId && current && current.role !== 'SLP') {
        setSelectedProviderId('');
      }
    }
  }, [docType, providers, selectedProviderId, isAssistantUser, user?.id, user?.email]);

  const selectedProvider = providers.find(p => p.id === selectedProviderId);
  const isSlpRequiredWorkflow = docType === 'EVAL' || docType === 'POC';
  const isEligibleAuthorSelected = Boolean(selectedProviderId) && (isAssistantUser
    ? (docType !== 'EVAL' && docType !== 'POC')
    : (!isSlpRequiredWorkflow || (selectedProvider?.role === 'SLP')));

  // Active Client Integrity: Only active clients are eligible for drafting new clinical documents.
  const selectableClients = useMemo(() => {
    return clients.filter(c => (c.status || 'ACTIVE').toUpperCase() === 'ACTIVE');
  }, [clients]);

  // Client-bound authoring state follows the selected chart only.
  useEffect(() => {
    if (!selectedClientId) return;
    const client = clients.find(c => c.id === selectedClientId) || null;
    const clientChanged = boundClientRef.current !== selectedClientId;
    if (clientChanged) {
      boundClientRef.current = selectedClientId;
      chartAppliedRef.current = client ? selectedClientId : '';
      authoringGoalsReadyRef.current = { clientId: selectedClientId, qpr: false, poc: false };
      const next = authoringForSelectedClient(client, {
        dos: initialDos,
        timeIn: initialStartTime,
        serviceCode: initialServiceCode
      });
      setEvalData(next.evalData);
      setPocData(next.pocData);
      setQprData(next.qprData);
      setSoapData(next.soapData);
      setClientGoals([]);
      setSoapTargetedGoals({});
      setSubjective('');
      setObjective('');
      setAssessment('');
      setPlan('');
      setMedicalNecessity(DEFAULT_SKILLED_NECESSITY);
      setDurationMinutes(60);
      setTimeOut(calculateTimeOut(initialStartTime || timeInRef.current || '09:00', 60));
    } else if (client && chartAppliedRef.current !== selectedClientId) {
      setEvalData(prev => withClientChartOnEval(prev, client));
      setPocData(prev => withClientChartOnPoc(prev, client));
      setQprData(prev => withClientChartOnQpr(prev, client));
      setSoapData(prev => withClientChartOnSoap(prev, client));
      chartAppliedRef.current = selectedClientId;
    }

    const requestToken = ++clientLoadTokenRef.current;
    const requestedClientId = selectedClientId;
    let cancelled = false;
    setLoadingGoals(true);
    fetch(`/api/clients/${requestedClientId}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled || !clientLoadIsCurrent(requestToken, clientLoadTokenRef.current) || boundClientRef.current !== requestedClientId) {
          return;
        }
        const goals = canonicalGoalsFromClientDetail(data?.goals);
        const soapTargets = soapTargetsFromClientGoals(goals);
        setClientGoals(goals);
        const ready = authoringGoalsReadyRef.current;
        const sameClient = ready.clientId === requestedClientId;
        if (!sameClient || !ready.poc) {
          setPocData(prev => ({ ...prev, goals: pocGoalsFromClientGoals(goals) }));
          authoringGoalsReadyRef.current = { ...authoringGoalsReadyRef.current, clientId: requestedClientId, poc: true };
        }
        if (!sameClient || !ready.qpr) {
          setQprData(prev => ({ ...prev, goals_review: qprReviewsFromClientGoals(goals) }));
          authoringGoalsReadyRef.current = { ...authoringGoalsReadyRef.current, clientId: requestedClientId, qpr: true };
        }
        setSoapTargetedGoals(soapTargets);

        const knownDuration =
          (data?.activeAssignment?.expected_duration_minutes && Number(data.activeAssignment.expected_duration_minutes) > 0)
            ? Number(data.activeAssignment.expected_duration_minutes)
            : (data?.assignments?.find((a: any) => a.expected_duration_minutes && Number(a.expected_duration_minutes) > 0)?.expected_duration_minutes)
              ? Number(data.assignments.find((a: any) => a.expected_duration_minutes && Number(a.expected_duration_minutes) > 0).expected_duration_minutes)
              : (data?.client?.expected_duration_minutes && Number(data.client.expected_duration_minutes) > 0)
                ? Number(data.client.expected_duration_minutes)
                : (client?.expected_duration_minutes && Number(client.expected_duration_minutes) > 0)
                  ? Number(client.expected_duration_minutes)
                  : null;

        const effectiveDuration = (knownDuration !== null && knownDuration > 0) ? knownDuration : 60;
        setDurationMinutes(effectiveDuration);
        setTimeOut(calculateTimeOut(timeInRef.current, effectiveDuration));
        setSoapData(prev => ({
          ...prev,
          targeted_goals: soapTargets,
          duration_minutes: effectiveDuration,
          time_out: calculateTimeOut(prev.time_in, effectiveDuration)
        }));
      })
      .catch(err => {
        if (!cancelled && clientLoadIsCurrent(requestToken, clientLoadTokenRef.current)) {
          console.error('Error fetching client goals:', err);
        }
      })
      .finally(() => {
        if (!cancelled && clientLoadIsCurrent(requestToken, clientLoadTokenRef.current)) {
          setLoadingGoals(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedClientId, clients, initialDos, initialStartTime, initialServiceCode]);

  // Update default document title when docType or client changes.
  // An edited SOAP title stays; only an untouched generated title follows the service code.
  useEffect(() => {
    const selectedClient = clients.find(c => c.id === selectedClientId);
    const clientName = selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Member';
    const today = new Date().toISOString().slice(0, 10);
    const enteredFromAnotherType = previousDocType.current !== null && previousDocType.current !== docType;
    previousDocType.current = docType;

    if (docType === 'EVAL') {
      setTitle(evalData.discipline === 'FEEDING'
        ? `Feeding Evaluation & Plan of Care - ${clientName} (${today})`
        : `Initial Speech-Language Evaluation & POC - ${clientName} (${today})`);
    } else if (docType === 'POC') {
      setTitle(pocData.discipline === 'FEEDING'
        ? `Feeding Plan of Care - ${clientName}`
        : `Speech Therapy Plan of Care Recertification - ${clientName}`);
    } else if (docType === 'QPR') {
      setTitle(`Quarterly Progress Report (${qprData.reporting_period_label || 'Q1 2026'}) - ${clientName}`);
    } else {
      const nextTitle = defaultSoapTitle({
        serviceCode: soapData.service_code,
        clientName,
        dos: soapData.dos || dos,
        style: 'modal'
      });
      setTitle(current => enteredFromAnotherType ? nextTitle : soapTitleAfterDefaultRefresh(current, nextTitle));
    }
  }, [docType, evalData.discipline, pocData.discipline, selectedClientId, clients, dos, soapData.dos, soapData.service_code, qprData.reporting_period_label]);

  // Recalculate duration when timeIn or timeOut changes
  useEffect(() => {
    if (timeIn && timeOut) {
      const [hIn, mIn] = timeIn.split(':').map(Number);
      const [hOut, mOut] = timeOut.split(':').map(Number);
      if (!isNaN(hIn) && !isNaN(mIn) && !isNaN(hOut) && !isNaN(mOut)) {
        const diff = (hOut * 60 + mOut) - (hIn * 60 + mIn);
        if (diff > 0) setDurationMinutes(diff);
      }
    }
  }, [timeIn, timeOut]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedClientId) {
      alert('Please select a client chart.');
      return;
    }

    if (!selectedProviderId) {
      alert('This account is not linked to a provider. Select the treating clinician before creating this document.');
      return;
    }

    if (isAssistantUser && (docType === 'EVAL' || docType === 'POC')) {
      alert('Speech-Language Pathology Assistants (SLPAs) are not permitted to author Evaluation or Plan of Care documents under Arizona DDD / AHCCCS regulations.');
      return;
    }

    if (isSlpRequiredWorkflow && !isEligibleAuthorSelected) {
      alert('Evaluation and Plan of Care documents require a licensed Speech-Language Pathologist (SLP) author.');
      return;
    }

    // Treatment-prescribing Eval / Initial POC must have at least one measurable goal
    if (docType === 'EVAL') {
      const evalSetting = parseAuthoredEvalTreatmentSetting(evalData.treatment_setting);
      if (!evalSetting.ok) {
        alert('Select Home / Community or Teletherapy before saving this evaluation. Setting cannot be assumed.');
        const sectionEl = document.getElementById('eval-section-setting');
        if (sectionEl) {
          sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      const planText = (plan || evalData.caregiver_training_plan || '').toLowerCase();
      const isDischargeOrNoTreatment =
        planText.includes('no treatment recommended') ||
        planText.includes('treatment not indicated') ||
        planText.includes('discharge from therapy');

      const validGoals = (evalData.established_goals || []).filter(g => g && g.description && g.description.trim().length > 0);
      if (!isDischargeOrNoTreatment && validGoals.length === 0) {
        alert('Validation Error: At least one measurable treatment goal is required in Section 7 (Initial Plan of Care) before saving this Evaluation.');
        const sectionEl = document.getElementById('eval-section-goals');
        if (sectionEl) {
          sectionEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
    }

    try {
      setSaving(true);

      let effectiveSectionData: any = null;
      let effectiveGoalsSnapshot: any[] = [];

      let effSubjective = subjective;
      let effObjective = objective;
      let effAssessment = assessment;
      let effPlan = plan;
      let effMedNecessity = medicalNecessity;
      let effRecommendations = '';
      let effPocStart = null;
      let effPocEnd = null;
      let effQprQuarter = null;
      let effPhysicianName = null;
      let effPhysicianNpi = null;
      let effPhysicianFax = null;

      if (docType === 'EVAL') {
        const evalSetting = parseAuthoredEvalTreatmentSetting(evalData.treatment_setting);
        if (!evalSetting.ok) {
          setSaving(false);
          return;
        }
        effectiveSectionData = { ...evalData, treatment_setting: evalSetting.value };
        effSubjective = evalData.client_context
          ? `${evalData.client_context}\n\nCaregiver Concerns: ${evalData.caregiver_concerns || 'None reported'}`
          : subjective;
        effObjective = `Receptive: ${evalData.receptive_language || 'WNL'}\nExpressive: ${evalData.expressive_language || 'WNL'}\nSpeech: ${evalData.speech_production || 'WNL'}\nStandardized Scores: ${evalData.standardized_tests?.map(t => `${t.test_name}: SS ${t.standard_score}`).join(', ') || 'Informal'}`;
        effAssessment = evalData.diagnostic_impressions || assessment;
        effPlan = `Frequency: ${evalData.prescribed_frequency || '2x/week'}, Duration: ${evalData.prescribed_duration || '60 min'}. Setting: ${evalSetting.value}. POC Window: ${evalData.poc_start_date} to ${evalData.poc_end_date}`;
        effMedNecessity = evaluationAuthoringSaveFields({
          discipline: evalData.discipline,
          medical_necessity_rationale: evalData.medical_necessity_rationale,
          speechDefault: medicalNecessity
        }).medical_necessity;
        effRecommendations = evalData.discharge_criteria || '';
        effPocStart = evalData.poc_start_date;
        effPocEnd = evalData.poc_end_date;
        effPhysicianName = evalData.physician_name;
        effPhysicianNpi = evalData.physician_npi;
        effPhysicianFax = evalData.physician_fax;
        effectiveGoalsSnapshot = (evalData.established_goals || []).map((g, idx) => ({
          goal_id: `GOL-INIT-${idx + 1}`,
          description: g.description,
          baseline: g.baseline,
          baseline_performance: g.baseline,
          target_percentage: g.target_percentage,
          target_date: g.target_date,
          goal_term: g.goal_term || undefined,
          status: 'IN_PROGRESS'
        }));
      } else if (docType === 'POC') {
        effectiveSectionData = pocData;
        effSubjective = pocData.progress_summary || subjective;
        effObjective = `Treatment Frequency: ${pocData.frequency || '2x/week'}. Active Goals: ${(pocData.goals || []).map(g => g.description).join('; ')}`;
        effAssessment = pocData.continued_skilled_need || assessment;
        effPlan = `POC Period: ${pocData.poc_start_date} to ${pocData.poc_end_date}. Setting: ${pocData.setting || 'Home'}.`;
        effMedNecessity = planMedicalNecessityForSave({
          discipline: pocData.discipline,
          statement: pocData.medical_necessity_statement,
          speechDefault: medicalNecessity
        });
        effPocStart = pocData.poc_start_date;
        effPocEnd = pocData.poc_end_date;
        effPhysicianName = pocData.physician_name;
        effPhysicianNpi = pocData.physician_npi;
        effPhysicianFax = pocData.physician_fax;
        effectiveGoalsSnapshot = (pocData.goals || []).map(g => ({
          id: g.id,
          description: g.description,
          baseline: g.baseline,
          baseline_performance: g.baseline,
          target_percentage: g.target_percentage,
          current_percentage: g.current_percentage,
          target_date: g.target_date,
          action: g.action,
          goal_term: g.goal_term || undefined,
          status: g.status || 'IN_PROGRESS'
        }));
      } else if (docType === 'QPR') {
        effectiveSectionData = qprData;
        effSubjective = qprData.collaboration_summary || subjective;
        effObjective = `Reporting Period: ${qprData.reporting_period_label}. Completed Sessions: ${qprData.attendance?.completed_sessions || 0}. Cancelled: ${qprData.attendance?.cancelled_sessions || 0}.`;
        effAssessment = qprData.therapy_summary ? `${qprData.therapy_summary}\n\nSkilled Need: ${qprData.skilled_care_justification || ''}` : assessment;
        effPlan = `Recommended Frequency: ${qprData.recommended_frequency || 'Continue 2x/week'}. Rationale: ${qprData.frequency_rationale || 'Functional communication carryover'}`;
        effMedNecessity = qprData.skilled_care_justification || medicalNecessity;
        effQprQuarter = qprData.reporting_period_label;
        effPhysicianName = qprData.physician_name;
        effPhysicianNpi = qprData.physician_npi;
        effectiveGoalsSnapshot = (qprData.goals_review || []).map(g => ({
          id: g.goal_id,
          description: g.description,
          baseline_performance: g.baseline,
          target_percentage: g.target_percentage,
          current_percentage: g.current_accuracy,
          progress_notes: g.functional_impact,
          status: g.recommendation === 'DISCONTINUE_MET' ? 'MET' : 'IN_PROGRESS'
        }));
      } else {
        // SOAP — Manual Exception Lane
        if (!isManualSoapApproved) {
          alert('Manual SOAP exception is not enabled for this client assignment. Please use Mobile SOAP at point of care or contact the Clinical Office.');
          setSaving(false);
          return;
        }

        if (!soapData.attestation_checked) {
          alert('Please confirm the treating clinician attestation checkbox before creating this Manual SOAP record.');
          setSaving(false);
          return;
        }

        const hasNarrative = Boolean(soapData.session_narrative.trim() || soapData.subjective.trim());
        if (!hasNarrative) {
          alert('Session Narrative is required to document client presentation, clinical response, and intervention.');
          setSaving(false);
          return;
        }

        if (!soapData.guardian_email.trim()) {
          alert('Signer / Guardian email address is required for the later Send to Guardian for eSign action.');
          setSaving(false);
          return;
        }

        if (
          soapData.clients_treated_together !== 1 &&
          soapData.clients_treated_together !== 2 &&
          soapData.clients_treated_together !== 3
        ) {
          alert('Select individual treatment (1 client) or group treatment (2 or 3 clients) before saving this encounter.');
          setSaving(false);
          return;
        }

        const targetedGoalsList = clientGoals
          .filter(g => soapData.targeted_goals[g.id]?.targeted)
          .map(g => {
            const gData = soapData.targeted_goals[g.id];
            return {
              goal_id: g.id,
              description: g.description,
              accuracy: gData?.accuracy ?? '',
              cueing: gData?.cueing || '',
              evidence: gData?.evidenceNote || ''
            };
          });

        const structuredGoalSummary = targetedGoalsList.length > 0
          ? [
              'Targeted Goals Addressed:',
              ...targetedGoalsList.map((g, i) => `${i + 1}. ${g.description} — Accuracy: ${g.accuracy}%, Cueing: ${g.cueing}${g.evidence ? ` [Trial evidence: ${g.evidence}]` : ''}`)
            ].join('\n')
          : '';

        const finalObjective = soapData.objective.trim()
          ? (structuredGoalSummary && !soapData.objective.includes('Targeted Goals Addressed')
              ? `${structuredGoalSummary}\n\nAdditional Objective Notes:\n${soapData.objective.trim()}`
              : soapData.objective.trim())
          : structuredGoalSummary;

        const finalSubjective = soapData.session_narrative.trim() || soapData.subjective.trim();
        const finalAssessment = '';
        const finalPlan = '';

        effSubjective = finalSubjective;
        effObjective = finalObjective;
        effAssessment = finalAssessment;
        effPlan = finalPlan;
        effMedNecessity = '';

        effectiveSectionData = {
          dos: soapData.dos,
          time_in: soapData.time_in,
          time_out: soapData.time_out,
          duration_minutes: soapData.duration_minutes,
          service_code: soapData.service_code,
          clients_treated_together: soapData.clients_treated_together,
          clinician_evidence: soapData.clinician_evidence,
          additional_observations: soapData.objective.trim(),
          session_narrative: soapData.session_narrative,
          targeted_goals: soapData.targeted_goals,
          guardian_name: soapData.guardian_name,
          guardian_email: soapData.guardian_email,
          guardian_relationship: soapData.guardian_relationship,
          attestation_checked: soapData.attestation_checked,
          is_manual_soap: true,
          esign_method: 'SIGNWELL_REMOTE'
        };

        effectiveGoalsSnapshot = targetedGoalsList.map(g => ({
          id: g.goal_id,
          description: g.description,
          performance_accuracy: g.accuracy,
          cueing_notes: g.cueing,
          trial_evidence: g.evidence,
          status: 'IN_PROGRESS'
        }));
      }

      const submittedDocType = authoredClinicalDocType(
        docType,
        docType === 'POC' ? pocData.discipline : docType === 'EVAL' ? evalData.discipline : undefined
      );

      const payload = {
        client_id: selectedClientId,
        provider_id: selectedProviderId || null,
        doc_type: submittedDocType,
        source: docType === 'SOAP' ? 'MANUAL_DESKTOP' : undefined,
        initiation_source: 'MANUAL_UI',
        guardian_name: docType === 'SOAP' ? soapData.guardian_name : undefined,
        guardian_email: docType === 'SOAP' ? soapData.guardian_email : undefined,
        guardian_relationship: docType === 'SOAP' ? soapData.guardian_relationship : undefined,
        title: title || `${docType} - ${docType === 'SOAP' ? soapData.dos : new Date().toISOString().split('T')[0]}`,
        subjective: effSubjective,
        objective: effObjective,
        assessment: effAssessment,
        plan: effPlan,
        medical_necessity: effMedNecessity,
        recommendations: effRecommendations,
        goals_addressed_json: JSON.stringify(effectiveGoalsSnapshot),
        eval_scores_json: docType === 'EVAL' && evalData.standardized_tests ? JSON.stringify(evalData.standardized_tests) : '{}',
        poc_start_date: effPocStart,
        poc_end_date: effPocEnd,
        qpr_quarter: effQprQuarter,
        section_data_json: effectiveSectionData ? JSON.stringify(effectiveSectionData) : undefined,
        physician_name: effPhysicianName,
        physician_npi: effPhysicianNpi,
        physician_fax: effPhysicianFax,
        initial_goals: docType === 'EVAL' ? (evalData.established_goals || []) : undefined
      };

      const res = await fetch('/api/clinical/docs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Initiation-Source': 'MANUAL_UI'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        playPulseSeal();

        onSuccess(data.docId);
        onClose();
      } else {
        const errData = await res.json().catch(() => ({}));
        if (errData.code === 'MANUAL_SOAP_NOT_PERMITTED') {
          alert('Manual SOAP is not enabled for this assignment. Use Mobile SOAP or contact Office.');
        } else {
          alert(errData.error || 'Failed to create clinical document (Server error).');
        }
      }
    } catch (err) {
      console.error('Error creating doc:', err);
    } finally {
      setSaving(false);
    }
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);
  const isDddSession = !selectedClient || selectedClient.payer_type === 'MEDICAID' ||
    (selectedClient.primary_payer_name && (
      selectedClient.primary_payer_name.toUpperCase().includes('DDD') ||
      selectedClient.primary_payer_name.toUpperCase().includes('DES') ||
      selectedClient.primary_payer_name.toUpperCase().includes('MEDICAID') ||
      selectedClient.primary_payer_name.toUpperCase().includes('AHCCCS')
    )) ||
    Boolean(selectedClient.ahcccs_id || selectedClient.wellsky_client_id);

  const isUnder53Minutes = docType === 'SOAP' && (soapData.service_code || '92507') === '92507' && soapData.duration_minutes < 53 && isDddSession;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 sm:bg-black/85 sm:backdrop-blur-sm flex flex-col sm:items-center sm:justify-center p-0 sm:p-4 overflow-y-auto">
      <div className="max-w-full sm:max-w-5xl w-full h-full sm:h-auto rounded-none sm:rounded-2xl border-0 sm:border border-slate-700/80 bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] shadow-[0_12px_40px_rgba(0,0,0,0.7),0_0_0_1px_rgba(148,163,184,0.08),inset_0_1px_0_rgba(255,255,255,0.08)] overflow-hidden my-0 sm:my-4 max-h-full sm:max-h-[94vh] flex flex-col">
        {/* Modal Top Header */}
        <div className="p-3.5 sm:p-4 bg-gradient-to-b from-[#1e2e48] to-[#152338] border-b border-slate-700/80 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2.5 sm:gap-3 shrink-0">
          <div className="flex items-center justify-between sm:justify-start space-x-2 sm:space-x-3 w-full sm:w-auto">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <button
                type="button"
                onClick={onClose}
                className="sm:hidden p-1.5 -ml-1 text-slate-400 hover:text-white flex items-center space-x-1 rounded-lg hover:bg-slate-800 transition-colors shrink-0"
                title="Back"
              >
                <ChevronLeft className="w-5 h-5 text-sky-400" />
                <span className="text-xs font-semibold">Back</span>
              </button>
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl border flex items-center justify-center transition-colors shrink-0 ${DOC_TYPE_THEMES[docType].headerIconBoxClasses}`}>
                {React.createElement(DOC_TYPE_THEMES[docType].icon, { className: 'w-4 h-4 sm:w-5 sm:h-5' })}
              </div>
              <div className="min-w-0">
                <h2 className="text-sm font-bold text-white flex items-center space-x-2 truncate">
                  <span>Create Document</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold border transition-colors shrink-0 ${DOC_TYPE_THEMES[docType].headerBadgeClasses}`}>
                    {DOC_TYPE_THEMES[docType].badgeLabel}
                  </span>
                </h2>
                <p className="text-[11px] text-slate-300 truncate hidden sm:block">
                  Select document type, client, and parameters.
                </p>
              </div>
            </div>

            <button
              onClick={onClose}
              className="sm:hidden p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors cursor-pointer shrink-0"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-center sm:justify-end space-x-2.5 sm:space-x-2 w-full sm:w-auto pt-2 sm:pt-0 border-t border-slate-700/60 sm:border-0">
            {/* Clinical Assist Launcher */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAiAssistMenu(prev => !prev)}
                className="px-3 py-1.5 rounded-full bg-[#f7f4ee] hover:bg-[#ece9e2] text-[#315875] text-xs font-bold flex items-center space-x-1.5 transition-colors cursor-pointer border border-[#c5d4e0]"
                title="Local section wording and evidence review"
              >
                <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-4 h-4 shrink-0 object-contain" />
                <span>Section Assist</span>
              </button>

              {/* Bloom Clinical Assist Popover Menu */}
              {showAiAssistMenu && (
                <div className="fixed inset-x-4 top-24 sm:absolute sm:inset-auto sm:right-0 sm:top-10 z-50 rounded-2xl bg-[#f8fafc] border border-slate-200 shadow-xl overflow-hidden text-slate-800 animate-in fade-in zoom-in-95 duration-150 sm:w-96">
                  <div className="p-3.5 bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border-b border-slate-700/80 flex items-center justify-between">
                    <div className="flex items-center space-x-2.5">
                      <div className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
                        <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className="w-5 h-5 shrink-0 object-contain" />
                      </div>
                      <div>
                        <h3 className="text-xs font-bold text-white flex items-center space-x-1.5">
                          <span>Section Assist</span>
                          <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-white/10 text-slate-100 font-semibold border border-white/20">
                            Grounded
                          </span>
                        </h3>
                        <p className="text-[10px] text-slate-300">Safe, evidence-based assistance</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowAiAssistMenu(false)}
                      className="p-1 rounded text-slate-300 hover:text-white hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Section Target Selector */}
                  <div className="p-3.5 border-b border-slate-200 bg-white space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-bold text-slate-700 uppercase tracking-wider">
                        Target Narrative Section
                      </label>
                      {selectedSectionKey && (
                        <button
                          type="button"
                          onClick={() => setSelectedSectionKey(null)}
                          className="text-[10px] text-slate-600 hover:text-slate-900 underline cursor-pointer"
                        >
                          Clear selection
                        </button>
                      )}
                    </div>
                    <select
                      value={selectedSectionKey || ''}
                      onChange={(e) => setSelectedSectionKey(e.target.value || null)}
                      className="w-full bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500 cursor-pointer"
                    >
                      <option value="" className="bg-white text-slate-800">-- No Section Selected --</option>
                      {compatibleSections.map((sec) => (
                        <option key={sec.key} value={sec.key} className="bg-white text-slate-800">
                          {sec.title} {sec.pairedKey ? '(Paired available)' : ''}
                        </option>
                      ))}
                    </select>
                    {!selectedSectionKey && (
                      <p className="text-[10px] text-amber-800 flex items-center space-x-1 pt-0.5">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        <span>No compatible field/section is currently selected.</span>
                      </p>
                    )}
                  </div>

                  {/* Actions List */}
                  <div className="p-3 space-y-2.5">
                    {/* 1. Refine Current Section */}
                    <div className="space-y-1">
                      <button
                        type="button"
                        disabled={!activeSectionConfig}
                        onClick={() => handleLaunchAssist('SINGLE')}
                        className={`w-full p-2.5 rounded-xl border text-left transition-all flex items-start space-x-2.5 ${
                          activeSectionConfig
                            ? 'bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 cursor-pointer text-slate-800 shadow-xs'
                            : 'bg-slate-50 border-slate-200 text-slate-600 cursor-not-allowed'
                        }`}
                      >
                        <img src="/brand/canonical/bloom-mark-micro.svg" alt="Bloom" className={`w-4 h-4 shrink-0 object-contain mt-0.5 ${activeSectionConfig ? '' : 'opacity-40'}`} />
                        <div className="flex-1">
                          <div className="font-bold text-xs flex items-center justify-between">
                            <span>Refine Current Section</span>
                            {activeSectionConfig && (
                              <span className="text-[10px] text-sky-800 font-normal">
                                {activeSectionConfig.title}
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-600 mt-0.5">
                            Refine active draft with clinician directives and chart facts
                          </p>
                        </div>
                      </button>
                      {!activeSectionConfig && (
                        <p className="text-[10px] text-slate-600 pl-2">
                          Disabled: Select or focus a clinical narrative section (e.g., Progress Summary, Subjective, Skilled Care Justification) to refine.
                        </p>
                      )}
                    </div>

                    {/* 2. Refine Related Fields */}
                    <div className="space-y-1">
                      <button
                        type="button"
                        disabled={!activeSectionConfig || !activeSectionConfig.pairedKey}
                        onClick={() => handleLaunchAssist('PAIRED')}
                        className={`w-full p-2.5 rounded-xl border text-left transition-all flex items-start space-x-2.5 ${
                          activeSectionConfig && activeSectionConfig.pairedKey
                            ? 'bg-white hover:bg-slate-50 border-slate-200 hover:border-slate-300 cursor-pointer text-slate-800 shadow-xs'
                            : 'bg-slate-50 border-slate-200 text-slate-600 cursor-not-allowed'
                        }`}
                      >
                        <Layers className={`w-4 h-4 shrink-0 mt-0.5 ${activeSectionConfig && activeSectionConfig.pairedKey ? 'text-sky-800' : 'text-slate-400'}`} />
                        <div className="flex-1">
                          <div className="font-bold text-xs flex items-center justify-between">
                            <span>Refine Related Fields</span>
                            {activeSectionConfig?.pairedKey && (
                              <span className="text-[10px] text-sky-800 font-normal">
                                Paired with {activeSectionConfig.pairedTitle}
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-slate-600 mt-0.5">
                            Review side-by-side proposals for companion clinical sections
                          </p>
                        </div>
                      </button>
                      {(!activeSectionConfig || !activeSectionConfig.pairedKey) && (
                        <p className="text-[10px] text-slate-600 pl-2">
                          {!activeSectionConfig
                            ? 'Disabled: Select a clinical section with paired fields to refine together.'
                            : 'Disabled: Selected section does not have paired companion fields.'}
                        </p>
                      )}
                    </div>

                    {/* 3. Review Missing Evidence */}
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => {
                          setShowAiAssistMenu(false);
                          setShowMissingEvidenceModal(true);
                        }}
                        className="w-full p-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 cursor-pointer text-slate-800 shadow-xs text-left transition-all flex items-start space-x-2.5"
                      >
                        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5 text-emerald-700" />
                        <div className="flex-1">
                          <div className="font-bold text-xs text-slate-900 flex items-center justify-between">
                            <span>Review Missing Evidence</span>
                            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200">
                              Safe Audit
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-600 mt-0.5">
                            Audit chart records for completed encounters, caregiver notes, and test scores
                          </p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="p-2.5 bg-slate-50 border-t border-slate-200 text-[10px] text-slate-600 text-center">
                    Local rules do not add scores, trial numbers, or routines that are not already documented.
                  </div>
                </div>
              )}
            </div>

            {/* Documentation Review Button (EVAL, POC, QPR) */}
            {docType !== 'SOAP' && (
              <button
                type="button"
                onClick={() => setShowComplianceAudit(true)}
                className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-bold flex items-center space-x-1.5 shadow transition-colors cursor-pointer"
                title="Run Documentation Compliance Review"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Review</span>
              </button>
            )}

            <button
              onClick={onClose}
              className="hidden sm:block p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors cursor-pointer"
              title="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Scrollable Form Body */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 overflow-y-auto space-y-5 text-xs flex-1 clinical-desktop-canvas clinical-structured-light text-slate-800">
          {/* Document Type Selector */}
          <div>
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-2">
              Document Type
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {[
                { id: 'EVAL' as const, label: 'Evaluation & POC', desc: 'Initial Evaluation & Plan of Care', slpOnly: true },
                { id: 'POC' as const, label: 'POC Recertification', desc: 'Plan of Care Recertification / Update', slpOnly: true },
                { id: 'QPR' as const, label: 'Quarterly Progress (QPR)', desc: 'Home Program & Goal Reviews' }
              ].map((t) => {
                const theme = DOC_TYPE_THEMES[t.id];
                const IconComponent = theme.icon;
                const isSelected = docType === t.id;
                const isDisabledForRole = isAssistantUser && t.slpOnly;
                return (
                  <button
                    type="button"
                    key={t.id}
                    disabled={isDisabledForRole}
                    onClick={() => {
                      if (!isDisabledForRole) {
                        setDocType(t.id);
                      }
                    }}
                    title={isDisabledForRole ? 'SLP Scope Requirement: SLPAs cannot author Evaluations or Plans of Care.' : undefined}
                    className={`relative p-3 rounded-xl border text-left transition-all flex flex-col justify-between overflow-hidden ${
                      isDisabledForRole
                        ? 'opacity-40 bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                        : isSelected
                        ? `${theme.tileSelectedBorder} ${theme.tileSelectedBg} ${theme.tileSelectedRing} ${theme.tileSelectedShadow} text-slate-900 cursor-pointer`
                        : 'bg-white border-slate-200 text-slate-800 hover:border-slate-300 hover:bg-slate-50 cursor-pointer shadow-xs'
                    }`}
                  >
                    {/* Subtle top specular highlight line when selected */}
                    {isSelected && (
                      <div className={`absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent ${theme.tileSpecularLine} to-transparent pointer-events-none`} />
                    )}
                    {/* Restrained accent glow blur when selected */}
                    {isSelected && (
                      <div className={`absolute -top-6 -right-6 w-20 h-20 ${theme.tileGlowBlur} rounded-full blur-lg pointer-events-none`} />
                    )}

                    <div className="flex items-center space-x-1.5 mb-1 z-10">
                      <IconComponent className={`w-4 h-4 transition-colors ${isSelected ? theme.tileIconColor : 'text-slate-500'}`} />
                      <span className="font-bold text-xs">{t.label}</span>
                      {isDisabledForRole && (
                        <span className="ml-auto text-[9px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 font-semibold">
                          SLP Only
                        </span>
                      )}
                    </div>
                    <div className={`text-[10px] z-10 ${isSelected ? 'text-slate-600' : 'text-slate-500'}`}>
                      {isDisabledForRole ? 'SLP author required under ASHA/DDD' : t.desc}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Client & Author Selection */}
          <div className="p-4 clinical-work-surface rounded-xl grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0 w-full">
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
                Client Chart *
              </label>
              <select
                value={selectedClientId}
                onChange={(e) => setSelectedClientId(e.target.value)}
                className="w-full min-w-0 box-border truncate bg-white border border-slate-200 rounded-lg px-3 py-2 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500 shadow-xs"
                required
              >
                {selectableClients.map((c) => {
                  const isNonActive = c.status && c.status.toUpperCase() !== 'ACTIVE';
                  const mobileLabel = `${c.last_name}, ${c.first_name}`;
                  const desktopLabel = `${c.last_name}, ${c.first_name} (ID: ${c.id} • ${c.primary_payer_name}${isNonActive ? ` • ${c.status}` : ''})`;
                  return (
                    <option key={c.id} value={c.id}>
                      {isMobile ? mobileLabel : desktopLabel}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="min-w-0 w-full">
              <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
                Authoring Treating Provider *
              </label>
              <select
                value={selectedProviderId}
                disabled={isAssistantUser}
                onChange={(e) => setSelectedProviderId(e.target.value)}
                className={`w-full min-w-0 box-border truncate bg-white border rounded-lg px-3 py-2 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-2 shadow-xs ${
                  isAssistantUser
                    ? 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                    : isSlpRequiredWorkflow && !isEligibleAuthorSelected
                    ? 'border-rose-500 focus:ring-rose-500/40 focus:border-rose-500'
                    : 'border-slate-200 focus:ring-sky-500/40 focus:border-sky-500'
                }`}
              >
                {!selectedProviderId && (
                  <option value="">{eligibleProviders.length === 0 ? 'No linked provider' : 'Select a treating clinician'}</option>
                )}
                {eligibleProviders.map((p) => {
                  const mobileLabel = p.role ? `${p.name} — ${p.role}` : p.name;
                  const desktopLabel = `${p.name} (${p.role} — ${p.title})`;
                  return (
                    <option key={p.id} value={p.id}>
                      {isMobile ? mobileLabel : desktopLabel}
                    </option>
                  );
                })}
              </select>
              {isAssistantUser && !selectedProviderId ? (
                <p className="mt-1 text-[10px] text-rose-700">
                  This account is not linked to a provider. Link the account before creating or signing a POC or QPR.
                </p>
              ) : !selectedProviderId ? (
                <p className="mt-1 text-[10px] text-rose-700">
                  Select the treating clinician. No provider is selected for this document.
                </p>
              ) : isAssistantUser ? (
                <p className="mt-1 text-[10px] text-slate-500">
                  Locked to your authenticated clinical profile.
                </p>
              ) : isSlpRequiredWorkflow && (
                <p className="mt-1 text-[10px] text-sky-700">
                  Evaluation and Plan of Care documents require an SLP author.
                </p>
              )}
            </div>
          </div>

          {/* Manual SOAP Guardrail Banner (when SOAP doc type is active) */}
          {docType === 'SOAP' && (
            <div className="space-y-2">
              {!isManualSoapApproved ? (
                <div className="p-4 rounded-xl border border-rose-200 bg-rose-50 text-rose-800 space-y-2">
                  <div className="flex items-center space-x-2 font-bold text-rose-800">
                    <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                    <span>Manual SOAP / Remote Signature Exception Lane Not Enabled</span>
                  </div>
                  <p className="text-xs text-rose-700 leading-relaxed">
                    Routine treatment SOAPs must continue through the standard Mobile SOAP workflow with point-of-care guardian signature.
                    Desktop / manual SOAP is an exception capability (Teletherapy, Center-Based ABA, etc.) that must be explicitly approved by Office or Admin for this client assignment.
                  </p>
                  <div className="text-[11px] font-semibold text-rose-800 bg-white px-3 py-1.5 rounded-lg border border-rose-200 inline-block">
                    Manual SOAP is not enabled for this assignment. Use Mobile SOAP or contact Office.
                  </div>
                </div>
              ) : (
                <div className="p-3.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 font-bold text-amber-900">
                      <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <span>Office-Approved Exception Lane Active</span>
                    </div>
                    <span className="px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-white text-amber-800 border border-amber-200">
                      {manualSoapReason || 'Operational Exception'}
                    </span>
                  </div>
                  <p className="text-xs text-amber-800">
                    Manual SOAP creation permitted for this assignment. After the note is saved, send the guardian request with Send to Guardian for eSign. Acknowledgment is due 24 hours after the session end in Arizona time.
                    {clientAssignment?.manual_soap_note && (
                      <span className="block mt-1 text-[11px] text-amber-700 italic">
                        Approval Note: "{clientAssignment.manual_soap_note}"
                      </span>
                    )}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Document Title */}
          <div className="p-4 clinical-work-surface rounded-xl min-w-0 w-full">
            <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
              Document Title *
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full min-w-0 box-border truncate bg-white border border-slate-200 rounded-lg px-3 py-2 text-base sm:text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500 font-semibold shadow-xs"
              required
            />
          </div>

          {/* ============================================================== */}
          {/* CLINICAL WORKSPACE CANVAS                                      */}
          {/* Lifted blue-grey / navy shell with deep ink readable fields    */}
          {/* ============================================================== */}
          <div className="rounded-2xl clinical-work-surface p-5 sm:p-6 space-y-6 text-slate-800">
            {/* Clinical Workspace Header Strip */}
            <div className="flex flex-wrap items-center justify-between pb-3 border-b border-slate-200 gap-2">
              <div className="flex items-center space-x-2">
                <span className="w-2.5 h-2.5 rounded-full bg-sky-500 shadow-xs"></span>
                {docType === 'QPR' ? (
                  <div className="flex items-baseline space-x-2">
                    <span className="font-bold text-xs uppercase tracking-wider text-slate-800">
                      Quarterly Progress Report
                    </span>
                    <span className="text-[10px] font-semibold text-slate-500 tracking-wider">
                      DDD-2063A
                    </span>
                  </div>
                ) : (
                  <span className="font-bold text-xs uppercase tracking-wider text-slate-800">
                    {docType === 'EVAL' ? 'Comprehensive Evaluation & Initial Plan of Care' :
                     docType === 'POC' ? 'Plan of Care' :
                     'Daily Treatment Encounter Documentation (SOAP)'}
                  </span>
                )}
              </div>
              <div className="flex items-center space-x-2 text-[11px] text-slate-600 font-medium">
                <span className="px-2 py-0.5 rounded bg-slate-50 border border-slate-200 font-mono text-slate-700 shadow-xs">
                  {selectedClient ? `${selectedClient.last_name}, ${selectedClient.first_name}` : 'Client Record'}
                </span>
                <span>•</span>
                <span>{selectedClient?.primary_payer_name || 'AHCCCS / DES-DDD'}</span>
              </div>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* WORKSPACE 1: EVALUATION & COMBINED INITIAL PLAN OF CARE       */}
            {/* ------------------------------------------------------------- */}
          {docType === 'EVAL' && (
            <EvalWorkspace
              data={evalData}
              onChange={setEvalData}
              client={selectedClient}
            />
          )}

          {/* ------------------------------------------------------------- */}
          {/* WORKSPACE 2: PLAN OF CARE RECERTIFICATION / UPDATE            */}
          {/* ------------------------------------------------------------- */}
          {docType === 'POC' && (
            <PocWorkspace
              data={pocData}
              onChange={setPocData}
              client={selectedClient}
              canonicalGoals={clientGoals}
            />
          )}

          {/* ------------------------------------------------------------- */}
          {/* WORKSPACE 3: QUARTERLY PROGRESS REPORT (QPR)                  */}
          {/* ------------------------------------------------------------- */}
          {docType === 'QPR' && (
            <QprWorkspace
              data={qprData}
              onChange={setQprData}
              client={selectedClient}
              canonicalGoals={clientGoals}
            />
          )}

          {/* ------------------------------------------------------------- */}
          {/* WORKSPACE 4: DAILY TREATMENT SOAP NOTE (MANUAL SOAP ALIGNED)  */}
          {/* ------------------------------------------------------------- */}
          {docType === 'SOAP' && (
            <ManualSoapWorkspace
              data={soapData}
              onChange={setSoapData}
              client={selectedClient || null}
              canonicalGoals={clientGoals}
              provider={selectedProvider || null}
              currentUser={user}
              isUnder53Minutes={isUnder53Minutes}
              isManualSoapApproved={isManualSoapApproved}
              manualSoapReason={manualSoapReason}
              manualSoapNote={clientAssignment?.manual_soap_note}
              onFocusSection={(secKey) => setSelectedSectionKey(secKey)}
            />
          )}
          </div>

          {/* Action Buttons */}
          <div className="pt-3 border-t border-slate-200 flex items-center justify-center sm:justify-end space-x-3 shrink-0 pb-[calc(16px+env(safe-area-inset-bottom,0px))] sm:pb-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-semibold transition-colors text-xs shadow-xs"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                saving ||
                !selectedClientId ||
                (isAssistantUser && (docType === 'EVAL' || docType === 'POC')) ||
                (docType === 'SOAP' && (!isManualSoapApproved || !soapData.attestation_checked)) ||
                !isEligibleAuthorSelected
              }
              className={`px-5 py-2 rounded-lg font-bold transition-colors shadow-md flex items-center space-x-2 text-xs ${
                (isAssistantUser && (docType === 'EVAL' || docType === 'POC')) ||
                (docType === 'SOAP' && (!isManualSoapApproved || !soapData.attestation_checked)) ||
                !isEligibleAuthorSelected
                  ? 'bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200'
                  : 'bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-[#ffffff] cursor-pointer'
              }`}
            >
              <Save className="w-4 h-4" />
              <span>
                {saving
                  ? 'Saving Document...'
                  : isAssistantUser && (docType === 'EVAL' || docType === 'POC')
                  ? 'SLPA Cannot Author EVAL/POC'
                  : docType === 'SOAP' && !isManualSoapApproved
                  ? 'Manual SOAP Disabled for Assignment'
                  : docType === 'SOAP' && !soapData.attestation_checked
                  ? 'Attestation Required to Create SOAP'
                  : !selectedProviderId
                  ? 'Provider link required'
                  : !isEligibleAuthorSelected
                  ? 'SLP Author Required'
                  : docType === 'SOAP'
                  ? 'Create & Dispatch Manual SOAP'
                  : `Create ${docType} Record`}
              </span>
            </button>
          </div>
        </form>
      </div>

      {/* DDD / AHCCCS Compliance Audit Modal */}
      {showComplianceAudit && (
        <ComplianceAuditModal
          docType={docType as 'EVAL' | 'POC' | 'QPR'}
          sectionData={docType === 'EVAL' ? evalData : docType === 'POC' ? pocData : qprData}
          clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client'}
          onClose={() => setShowComplianceAudit(false)}
          onApplyCorrections={(corrections) => {
            if (docType === 'EVAL') {
              setEvalData(prev => ({ ...prev, ...corrections }));
            } else if (docType === 'POC') {
              setPocData(prev => ({ ...prev, ...corrections }));
            } else if (docType === 'QPR') {
              setQprData(prev => ({ ...prev, ...corrections }));
            }
          }}
        />
      )}

      {/* Grounded AI Refine Modal launched from top-right AI Clinical Assist */}
      {refineModalState && (
        <AiRefineModal
          isOpen={refineModalState.isOpen}
          onClose={() => setRefineModalState(null)}
          docType={authoredClinicalDocType(
            docType,
            docType === 'POC' ? pocData.discipline : docType === 'EVAL' ? evalData.discipline : undefined
          )}
          clientId={selectedClientId}
          reportingPeriod={docType === 'QPR' ? qprData.qpr_quarter : undefined}
          sectionKey={refineModalState.sectionKey}
          sectionTitle={refineModalState.sectionTitle}
          currentText={refineModalState.currentText}
          clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client'}
          pairedFields={refineModalState.pairedFields}
          initialMode={refineModalState.initialMode}
          onApply={(refined) => {
            handleApplySectionText(refineModalState.sectionKey, refined);
            setRefineModalState(null);
          }}
          onApplyField={(key, refined) => {
            handleApplySectionText(key, refined);
            setRefineModalState(null);
          }}
          onApplyPaired={(updates) => {
            Object.entries(updates).forEach(([k, v]) => {
              handleApplySectionText(k, v as string);
            });
            setRefineModalState(null);
          }}
        />
      )}

      {/* Missing Evidence Audit Modal */}
      {showMissingEvidenceModal && (
        <MissingEvidenceModal
          isOpen={showMissingEvidenceModal}
          onClose={() => setShowMissingEvidenceModal(false)}
          clientId={selectedClientId}
          clientName={selectedClient ? `${selectedClient.first_name} ${selectedClient.last_name}` : 'Client'}
          docType={docType}
          reportingPeriod={docType === 'QPR' ? qprData.qpr_quarter : undefined}
        />
      )}
    </div>
  );
};
